import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "utils/axios";
import { GET_VENDOR_TAXONOMY_URL } from "config/services";

const normalizeText = (value = "") =>
  value.toString().trim().toLowerCase();

const formatTaxonomyLabel = (value = "") =>
  value
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getId = (value) => value?.toString?.() || "";

const normalizeSelectedSubCategories = (category = {}) => {
  const nestedSubCategories = Array.isArray(category?.categories)
    ? category.categories.flatMap((selectedCategory) => selectedCategory?.subCategories || [])
    : [];
  const subCategories = Array.isArray(category?.subCategories) ? category.subCategories : nestedSubCategories;
  const normalized = subCategories
    .map((subCategory) => ({
      subCategoryId: getId(subCategory?.subCategoryId || subCategory?._id),
      subCategoryName: subCategory?.subCategoryName || subCategory?.name || "",
    }))
    .filter((subCategory) => subCategory.subCategoryId || subCategory.subCategoryName);

  if (normalized.length > 0) return normalized;

  if (category?.subCategoryId || category?.subCategoryName) {
    return [{
      subCategoryId: getId(category.subCategoryId),
      subCategoryName: category.subCategoryName || "",
    }];
  }

  return [];
};

const normalizeSelectedCategories = (category = {}) => {
  const selectedCategories = Array.isArray(category?.categories) ? category.categories : [];
  const normalized = selectedCategories
    .map((selectedCategory) => ({
      categoryId: getId(selectedCategory?.categoryId || selectedCategory?._id),
      categoryName: selectedCategory?.categoryName || selectedCategory?.name || "",
      subCategories: normalizeSelectedSubCategories(selectedCategory),
    }))
    .filter((selectedCategory) => selectedCategory.categoryId || selectedCategory.categoryName);

  if (normalized.length > 0) return normalized;

  if (category?.categoryId || category?.categoryName) {
    return [{
      categoryId: getId(category.categoryId),
      categoryName: category.categoryName || "",
      subCategories: normalizeSelectedSubCategories(category),
    }];
  }

  return [];
};

const buildSelectedSubject = (categoryName = "", subCategories = []) => {
  const selectedNames = subCategories
    .map((subCategory) => subCategory.subCategoryName)
    .filter(Boolean);

  return selectedNames.length ? selectedNames.join(", ") : categoryName;
};

const buildCategorySelection = (result, subCategories = []) => {
  const selectedSubCategories = Array.isArray(subCategories) ? subCategories : [];

  return {
    taxonomyType: result.taxonomyType || "",
    disciplineId: result.disciplineId,
    disciplineName: result.disciplineName,
    categoryName: result.categoryName,
    categoryId: result.categoryId,
    subCategoryId: selectedSubCategories[0]?.subCategoryId || "",
    subCategoryName: selectedSubCategories[0]?.subCategoryName || "",
    subCategories: selectedSubCategories,
    categories: [{
      categoryId: result.categoryId,
      categoryName: result.categoryName,
      subCategories: selectedSubCategories,
    }],
  };
};

const buildMultiCategorySubject = (categories = []) =>
  categories
    .map((category) => category.categoryName)
    .filter(Boolean)
    .join(", ");

const buildMultiCategorySelection = (result, categories = []) => {
  const firstCategory = categories[0] || {
    categoryId: result.categoryId,
    categoryName: result.categoryName,
    subCategories: [],
  };

  return {
    taxonomyType: result.taxonomyType || "",
    disciplineId: result.disciplineId,
    disciplineName: result.disciplineName,
    categoryName: firstCategory.categoryName || "",
    categoryId: firstCategory.categoryId || "",
    subCategoryId: "",
    subCategoryName: "",
    subCategories: [],
    categories: categories.map((category) => ({
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      subCategories: [],
    })),
  };
};

const getSelectorLabel = (vendorType = "") =>
  shouldUseAllTaxonomies(vendorType) ? "category" : `${vendorType.toLowerCase()} category`;

const taxonomyBadgeClass = (type = "", selected = false) => {
  if (selected) return "border border-green-200 bg-lime-50 text-green-700";

  return normalizeText(type) === "subcategory"
    ? "border border-teal-100 bg-teal-50 text-teal-700"
    : "border border-amber-100 bg-amber-50 text-amber-700";
};

const selectedChipClass =
  "inline-flex max-w-full items-center gap-1 rounded-full border border-green-200 bg-lime-50 px-2 py-0.5 text-[12px] font-medium text-green-800";

const selectedChipButtonClass = "text-green-800 hover:text-green-800 ";

const categoryResultClass = (selected = false) =>
  selected ? "" : "hover:bg-blue-50";

const shouldUseAllTaxonomies = (vendorType = "") =>
  !vendorType || normalizeText(vendorType) === "all";

const getTaxonomiesByVendorType = (taxonomyAll = {}, vendorType = "") => {
  if (shouldUseAllTaxonomies(vendorType)) {
    return Object.entries(taxonomyAll || {})
      .filter(([, taxonomy]) => taxonomy?.disciplines?.length)
      .map(([key, taxonomy]) => ({ key, taxonomy }));
  }

  const matchingKey = Object.keys(taxonomyAll || {}).find(
    (key) => normalizeText(key) === normalizeText(vendorType)
  );

  return matchingKey ? [{ key: matchingKey, taxonomy: taxonomyAll[matchingKey] }] : [];
};

const buildCategoryResults = (taxonomyAll = {}, searchTerm = "", vendorType = "", preferredTaxonomyType = "") => {
  const normalizedSearch = normalizeText(searchTerm);
  const preferredType = normalizeText(preferredTaxonomyType);
  // Prioritize the RFQ's hierarchy before applying suggestion limits.
  const taxonomies = getTaxonomiesByVendorType(taxonomyAll, vendorType).sort(
    (left, right) => Number(normalizeText(right.key) === preferredType) - Number(normalizeText(left.key) === preferredType)
  );

  if (!normalizedSearch || !taxonomies.length) return [];

  const results = [];

  taxonomies.forEach(({ key: taxonomyType, taxonomy }) => {
    const taxonomyResults = [];

    taxonomy.disciplines.forEach((discipline) => {
      (discipline?.categories || []).forEach((category) => {
        const categoryName = category?.name || "";
        const disciplineName = discipline?.name || "";
        const categoryMatchesSearch = normalizeText(categoryName).includes(normalizedSearch);
        const disciplineMatchesSearch = normalizeText(disciplineName).includes(normalizedSearch);
        const categoryResult = {
          id: getId(category?._id),
          label: categoryName,
          categoryId: getId(category?._id),
          categoryName,
          disciplineId: getId(discipline?._id),
          disciplineName,
          path: [taxonomyType, disciplineName, categoryName].filter(Boolean).join(" > "),
          type: "Category",
          taxonomyType,
        };

        if (
          categoryMatchesSearch ||
          disciplineMatchesSearch
        ) {
          taxonomyResults.push(categoryResult);
        }

        (category?.subCategories || []).forEach((subCategory) => {
          const subCategoryName = subCategory?.name || "";

          if (
            !categoryMatchesSearch &&
            !disciplineMatchesSearch &&
            !normalizeText(subCategoryName).includes(normalizedSearch)
          ) {
            return;
          }

          taxonomyResults.push({
            ...categoryResult,
            id: getId(subCategory?._id),
            label: subCategoryName,
            subCategoryId: getId(subCategory?._id),
            subCategoryName,
            path: [taxonomyType, disciplineName, categoryName, subCategoryName].filter(Boolean).join(" > "),
            type: "Subcategory",
          });
        });
      });
    });

    results.push(...taxonomyResults.slice(0, 10));
  });

  return results.slice(0, 20);
};

const buildSelectedCategoryResults = (taxonomyAll = {}, selectedCategory = {}, vendorType = "") => {
  if (!selectedCategory?.categoryId && !selectedCategory?.disciplineId) return [];

  const taxonomies = getTaxonomiesByVendorType(taxonomyAll, selectedCategory.taxonomyType || vendorType);
  const results = [];
  const selectedSubCategories = normalizeSelectedSubCategories(selectedCategory);
  const selectedCategories = normalizeSelectedCategories(selectedCategory);
  const selectedDisciplineId = selectedCategory.disciplineId;
  const isSubCategoryMode = selectedSubCategories.length > 0;

  taxonomies.forEach(({ key: taxonomyType, taxonomy }) => {
    (taxonomy?.disciplines || []).forEach((discipline) => {
      if (selectedDisciplineId && getId(discipline?._id) !== getId(selectedDisciplineId)) return;

      (discipline?.categories || []).forEach((category) => {
        if (isSubCategoryMode && getId(category?._id) !== getId(selectedCategory.categoryId)) return;

        const disciplineName = discipline?.name || selectedCategory.disciplineName || "";
        const categoryName = category?.name || selectedCategory.categoryName || "";
        const categoryResult = {
          id: getId(category?._id),
          label: categoryName,
          categoryId: getId(category?._id),
          categoryName,
          disciplineId: getId(discipline?._id) || selectedCategory.disciplineId || "",
          disciplineName,
          path: [taxonomyType, disciplineName, categoryName].filter(Boolean).join(" > "),
          type: "Category",
          taxonomyType,
        };

        if (!isSubCategoryMode) {
          results.push(categoryResult);
          return;
        }

        (category?.subCategories || []).forEach((subCategory) => {
          const subCategoryName = subCategory?.name || "";
          results.push({
            ...categoryResult,
            id: getId(subCategory?._id),
            label: subCategoryName,
            subCategoryId: getId(subCategory?._id),
            subCategoryName,
            path: [taxonomyType, disciplineName, categoryName, subCategoryName].filter(Boolean).join(" > "),
            type: "Subcategory",
          });
        });
      });
    });
  });

  const selectedCategoryIds = new Set(selectedCategories.map((category) => getId(category.categoryId)));

  return results
    .sort((a, b) => Number(selectedCategoryIds.has(getId(b.categoryId))) - Number(selectedCategoryIds.has(getId(a.categoryId))))
    .slice(0, 20);
};

const RfqTaxonomySubjectSelector = ({
  error,
  helperText,
  onCategoryChange,
  onSubjectChange,
  selectedCategory,
  subject,
  vendorType = "Material",
  preferredTaxonomyType = "",
}) => {
  const [taxonomyAll, setTaxonomyAll] = useState({});
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState(subject || "");
  const wrapperRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTaxonomy = async () => {
      try {
        setLoading(true);
        setFetchFailed(false);
        const response = await axios.get(GET_VENDOR_TAXONOMY_URL);
        if (!isMounted) return;
        setTaxonomyAll(response?.data?.data || {});
      } catch (fetchError) {
        if (!isMounted) return;
        setFetchFailed(true);
        setTaxonomyAll({});
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTaxonomy();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedCategory?.categoryId) return;

    const selectedSubCategories = normalizeSelectedSubCategories(selectedCategory);
    const selectedCategories = normalizeSelectedCategories(selectedCategory);
    const taxonomies = getTaxonomiesByVendorType(taxonomyAll, selectedCategory.taxonomyType || vendorType);
    const categoryExists = taxonomies.some(({ taxonomy }) => {
      const selectedDiscipline = (taxonomy?.disciplines || []).find(
        (discipline) => getId(discipline?._id) === getId(selectedCategory.disciplineId)
      );

      if (!selectedDiscipline) return false;

      return selectedCategories.every((selectedCategoryNode) =>
        (selectedDiscipline?.categories || []).some((category) => {
          const categoryMatches = getId(category?._id) === getId(selectedCategoryNode.categoryId);

          if (!categoryMatches) return false;
          if (!selectedSubCategories.length) return true;

          return selectedCategoryNode.subCategories.every((selectedSubCategory) =>
            (category?.subCategories || []).some(
              (subCategory) => getId(subCategory?._id) === getId(selectedSubCategory.subCategoryId)
            )
          );
        })
      );
    });

    if (taxonomies.length && !categoryExists) {
      onCategoryChange(null);
    }
  }, [onCategoryChange, selectedCategory, taxonomyAll, vendorType]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!wrapperRef.current || wrapperRef.current.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (selectedCategory?.categoryId) return;
    setSearchTerm(subject || "");
  }, [selectedCategory?.categoryId, subject]);

  const selectedSubCategories = useMemo(
    () => normalizeSelectedSubCategories(selectedCategory),
    [selectedCategory]
  );
  const selectedCategories = useMemo(
    () => normalizeSelectedCategories(selectedCategory),
    [selectedCategory]
  );
  const isSubCategoryMode = selectedSubCategories.length > 0;
  const hasSelection = Boolean(selectedCategory?.categoryId || selectedCategory?.disciplineId);

  const results = useMemo(
    () => {
      if (searchTerm) {
        const searchResults = buildCategoryResults(taxonomyAll, searchTerm, vendorType, preferredTaxonomyType);

        if (!selectedCategory?.categoryId) return searchResults;

        if (isSubCategoryMode) {
          return searchResults.filter(
            (result) => result.subCategoryId && getId(result.categoryId) === getId(selectedCategory.categoryId)
          );
        }

        return searchResults.filter(
          (result) => !result.subCategoryId && getId(result.disciplineId) === getId(selectedCategory.disciplineId)
        );
      }

      return buildSelectedCategoryResults(taxonomyAll, selectedCategory, vendorType);
    },
    [isSubCategoryMode, preferredTaxonomyType, searchTerm, selectedCategory, taxonomyAll, vendorType]
  );

  const groupedResults = useMemo(() => {
    const groups = [];
    const groupMap = new Map();

    results.forEach((result) => {
      const groupKey = normalizeText(result.taxonomyType || vendorType || "category");
      let group = groupMap.get(groupKey);

      if (!group) {
        group = {
          key: groupKey,
          label: formatTaxonomyLabel(result.taxonomyType || vendorType || "Category"),
          results: [],
        };
        groupMap.set(groupKey, group);
        groups.push(group);
      }

      group.results.push(result);
    });

    return groups;
  }, [results, vendorType]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (!selectedCategory?.categoryId) {
      onSubjectChange(value);
      onCategoryChange(null);
    }
    setIsOpen(true);
  };

  const clearCategorySelection = () => {
    onSubjectChange("");
    onCategoryChange(null);
    setSearchTerm("");
    setIsOpen(false);
  };

  const removeSubCategory = (subCategoryId) => {
    const nextSubCategories = selectedSubCategories.filter(
      (subCategory) => getId(subCategory.subCategoryId) !== getId(subCategoryId)
    );
    const nextCategory = buildCategorySelection(selectedCategory, nextSubCategories);

    onSubjectChange(buildSelectedSubject(selectedCategory?.categoryName, nextSubCategories));
    onCategoryChange(nextCategory);
    setSearchTerm("");
    setIsOpen(false);
  };

  const removeCategory = (categoryId) => {
    const nextCategories = selectedCategories.filter(
      (category) => getId(category.categoryId) !== getId(categoryId)
    );

    if (!nextCategories.length) {
      clearCategorySelection();
      return;
    }

    const nextCategory = buildMultiCategorySelection(selectedCategory, nextCategories);
    onSubjectChange(buildMultiCategorySubject(nextCategories));
    onCategoryChange(nextCategory);
    setSearchTerm("");
    setIsOpen(false);
  };

  const isResultSelected = (result) => {
    if (result?.subCategoryId) {
      return (
        getId(result.categoryId) === getId(selectedCategory?.categoryId) &&
        selectedSubCategories.some(
          (subCategory) => getId(subCategory.subCategoryId) === getId(result.subCategoryId)
        )
      );
    }

    return selectedCategories.some(
      (category) => getId(category.categoryId) === getId(result?.categoryId)
    );
  };

  const handleSelectCategory = (result) => {
    if (!result.subCategoryId) {
      const sameDiscipline = getId(selectedCategory?.disciplineId) === getId(result.disciplineId);
      const currentCategories = sameDiscipline && !isSubCategoryMode ? selectedCategories : [];
      const alreadySelected = currentCategories.some(
        (category) => getId(category.categoryId) === getId(result.categoryId)
      );
      const nextCategories = alreadySelected
        ? currentCategories.filter((category) => getId(category.categoryId) !== getId(result.categoryId))
        : [
          ...currentCategories,
          {
            categoryId: result.categoryId,
            categoryName: result.categoryName,
            subCategories: [],
          },
        ];

      if (!nextCategories.length) {
        clearCategorySelection();
        return;
      }

      onSubjectChange(buildMultiCategorySubject(nextCategories));
      onCategoryChange(buildMultiCategorySelection(result, nextCategories));
      setSearchTerm("");
      setIsOpen(false);
      return;
    }

    const sameCategory = getId(selectedCategory?.categoryId) === getId(result.categoryId);
    const currentSubCategories = sameCategory ? selectedSubCategories : [];
    const alreadySelected = currentSubCategories.some(
      (subCategory) => getId(subCategory.subCategoryId) === getId(result.subCategoryId)
    );
    const nextSubCategories = alreadySelected
      ? currentSubCategories.filter(
        (subCategory) => getId(subCategory.subCategoryId) !== getId(result.subCategoryId)
      )
      : [
        ...currentSubCategories,
        {
          subCategoryId: result.subCategoryId,
          subCategoryName: result.subCategoryName,
        },
      ];

    onSubjectChange(buildSelectedSubject(result.categoryName, nextSubCategories));
    onCategoryChange(buildCategorySelection(result, nextSubCategories));
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="flex justify-between ">
      <label htmlFor="rfqName" className="mb-1 block text-sm font-medium">
        RFQ Category (Subject)<span className="ml-0.5 text-red-500">*</span>
      </label>
        {hasSelection && (
          <button
            type="button"
            className="ml-auto my-1 shrink-0 rounded-full border border-gray-200 bg-white px-1 py-0 text-[10px] font-semibold text-gray-600 transition-colors hover:border-orange-500 hover:bg-orange-500 hover:text-white"
            onMouseDown={(event) => event.preventDefault()}
            onClick={clearCategorySelection}
          >
            Clear All
          </button>
        )}
        </div>
      <div
        className={`flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-sm border bg-white px-2 py-1 ${
          error ? "border-red-400" : "border-gray-300"
        }`}
      >
        {selectedCategory?.categoryId && isSubCategoryMode && (
          <span className={selectedChipClass}>
            <span className="truncate">
              {[selectedCategory.disciplineName, selectedCategory.categoryName]
                .filter(Boolean)
                .join(" > ") || subject}
            </span>
            <button
              type="button"
              className={selectedChipButtonClass}
              onClick={clearCategorySelection}
              aria-label={`Remove ${selectedCategory.categoryName || "selected category"}`}
            >
              x
            </button>
          </span>
        )}
        {selectedCategory?.disciplineId && !isSubCategoryMode && selectedCategories.length > 0 && (
          <span className={selectedChipClass}>
            <span className="truncate">{selectedCategory.disciplineName || "Selected discipline"}</span>
            <button
              type="button"
              className={selectedChipButtonClass}
              onClick={clearCategorySelection}
              aria-label="Remove selected discipline categories"
            >
              x
            </button>
          </span>
        )}
        {!isSubCategoryMode && selectedCategories.map((category) => (
          <span
            key={category.categoryId || category.categoryName}
            className={selectedChipClass}
          >
            <span className="truncate">{category.categoryName}</span>
            <button
              type="button"
              className={selectedChipButtonClass}
              onClick={() => removeCategory(category.categoryId)}
              aria-label={`Remove ${category.categoryName}`}
            >
              x
            </button>
          </span>
        ))}
        {selectedSubCategories.map((subCategory) => (
          <span
            key={subCategory.subCategoryId || subCategory.subCategoryName}
            className={selectedChipClass}
          >
            <span className="truncate">{subCategory.subCategoryName}</span>
            <button
              type="button"
              className={selectedChipButtonClass}
              onClick={() => removeSubCategory(subCategory.subCategoryId)}
              aria-label={`Remove ${subCategory.subCategoryName}`}
            >
              x
            </button>
          </span>
        ))}
        <input
          id="rfqName"
          name="rfqName"
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={
            selectedCategory?.categoryId
              ? isSubCategoryMode
                ? `Search/add more ${selectedCategory.categoryName || "category"} subcategories`
                : `Search/add more ${selectedCategory.disciplineName || "discipline"} categories`
              : `Search ${getSelectorLabel(vendorType)}`
          }
          className="h-7 min-w-[150px] flex-1 border-0 bg-transparent px-1 text-sm font-normal focus:outline-none focus:ring-0"
          autoComplete="off"
        />
        
        {loading && (
          <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-orange-100 border-t-orange-500" />
        )}
      </div>

      {helperText && <p className="mt-1 text-xs text-red-500">{helperText}</p>}

      {isOpen && (searchTerm || selectedCategory?.categoryId) && !loading && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
          {fetchFailed ? (
            <div className="px-4 py-3 text-xs text-red-600">
              Could not load categories. Please try again before creating the RFQ.
            </div>
          ) : results.length > 0 ? (
            <div className="max-h-72 overflow-y-auto">
              <div className="border-b border-gray-100 bg-[#FBFBF9] px-4 py-2 text-[11px] text-gray-500">
                Select a matching category for better vendor recommendations
              </div>
              {groupedResults.map((group) => (
                <div key={group.key}>
                  <div className="sticky top-0 z-10 border-b border-orange-100 bg-[#FFF8F2] px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-[#C45100]">
                    {group.label}
                  </div>
                  {group.results.map((result) => {
                    const selected = isResultSelected(result);

                    return (
                      <button
                        key={`${result.categoryId}-${result.path}`}
                        type="button"
                        className={`flex w-full items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 ${categoryResultClass(selected)}`}
                        onClick={() => handleSelectCategory(result)}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-[#1F2937]">
                            {result.label}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-gray-500">
                            {result.path}
                          </span>
                        </span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${taxonomyBadgeClass(result.type, selected)}`}>
                          {selected ? "Selected" : result.type || vendorType}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-3 text-xs text-gray-500">
              No matching category found. Try another keyword and select a suggestion.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RfqTaxonomySubjectSelector;
